import { uuidModule } from "@/modules/uuid";
import { EventContext } from "@/domains/metrics/context";
import { NoteSuggestionStatus } from "@/store/note/suggestion-data";
import {
  SpaceAccountNoteModelData,
  SpaceAccountNoteUpsertedSyncUpdateValue,
} from "@/store/recent-items/types";
import { NoteSuggestionData } from "@/store/note/types";
import {
  BaseSyncOperation,
  BaseSyncOperationParams,
} from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IUpsertNoteSuggestionOperation } from "@/store/sync/operations/types";
import { SyncModelData, OptimisticSyncUpdate, SyncOperationKind } from "@/store/sync/types";

export class UpsertNoteSuggestionOperation extends BaseSyncOperation<IUpsertNoteSuggestionOperation> {
  private readonly suggestionId: string;

  constructor(
    params: BaseSyncOperationParams<IUpsertNoteSuggestionOperation> & {
      payload: BaseSyncOperationParams<IUpsertNoteSuggestionOperation>["payload"] & {
        suggestion_id: NonNullable<IUpsertNoteSuggestionOperation["payload"]["suggestion_id"]>;
      };
      eventContext?: EventContext;
    }
  ) {
    super(params);
    this.suggestionId = params.payload.suggestion_id;
  }

  get operationKind(): SyncOperationKind {
    return "UPSERT_NOTE_SUGGESTION";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    const spaceAccountNote = await this.store.spaceAccountNotes.getAsync(spaceAccountNoteId);
    if (!spaceAccountNote) return [];

    const suggestionData = {
      id: this.suggestionId,
      status: NoteSuggestionStatus.Pending,
      kind: this.payload.kind,
      suggested_encoded_content: this.payload.suggested_encoded_content,
      suggested_encoded_content_diff: this.payload.suggested_encoded_content_diff,
      prev_encoded_content: this.payload.prev_encoded_content,
      prev_primary_label: this.payload.prev_primary_label,
      prev_secondary_label: this.payload.prev_secondary_label,
      prev_markdown_content: this.payload.prev_markdown_content,
      suggested_primary_label: this.payload.suggested_primary_label,
      suggested_secondary_label: this.payload.suggested_secondary_label,
      suggested_markdown_content: this.payload.suggested_markdown_content,
      metadata: this.payload.metadata,
    } as NoteSuggestionData;

    const spaceAccountNoteUpsertedValue: SpaceAccountNoteUpsertedSyncUpdateValue = {
      model_id: spaceAccountNoteId,
      model_kind: "SPACE_ACCOUNT_NOTE",
      model_version: spaceAccountNote?.modelVersion ?? 0,
      model_data: {
        ...spaceAccountNote.modelData,
        suggestion_data: suggestionData,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };

    const spaceAccountNoteUpserted: OptimisticSyncUpdate<SpaceAccountNoteModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: spaceAccountNoteUpsertedValue,
    };

    return [spaceAccountNoteUpserted];
  }

  async triggerRecompute() {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    await this.store.spaceAccountNotes.recompute(spaceAccountNoteId);
    await this.store.notes.recompute(this.payload.note_id);
  }
}
